import React, { FunctionComponent } from "react";

import {
    ReportH1,
    ReportScrollableSection,
    ReportChartGroup,
    sortMergedChartData,
    sortUYN,
    formatUYN,
    formatMarriedSingle,
    ReportSectionNumber,
    sortEducation,
    formatEducation,
    sortDwellingType,
    ReportSectionHeader,
    AnimatedReportCard,
    formatDwelling,
    ThreeColumnGrid,
} from "../../../Common/";
import { colorTwoBright, colorTwoDark } from "../../../../globalStyles";
import { IRetailMatchChartSection } from "../../../../Models/Models";
import { mergeRetailMatchData } from "../Logic/SharedMethods";
import { TextFocus } from "../Styled/RetailMatchHouseholdCompositionStyles";
import { DigitalShareAppType } from "../../../../Enums/DigitalShareAppType";
import { DigitalShareItemType } from "../../../../Enums/DigitalShareItemType";
import { CHILD_HEIGHT } from "../../../Common/DigitalShare/DigitalShareGenerator";
import { RenderType } from "../../../../Enums/RenderType";
import { HH_ATTR_STATIC_TEXT } from "../../../../Util/ScoutDigitalShareUtil";
import { brandOverviewRecord } from "../../../../Util/RetailMatch/RetailMatchUtil";

export const getChartText = (
    sampleCount: number,
    locationAddress: string,
    brandName: string
): JSX.Element => {
    return (
        <div
            style={{
                display: "flex",
                flexDirection: "column",
                justifyContent: "end",
            }}
        >
            <span style={{ fontWeight: 900, marginBottom: 5 }}>
                Household characteristics of {locationAddress}{" "}
                <span
                    data-testid="RM-HH-chart-text-COMPARED-TO"
                    style={{ fontWeight: 400 }}
                >
                    COMPARED TO
                </span>{" "}
                {sampleCount} existing similar {brandName} locations.
            </span>
            <span>{HH_ATTR_STATIC_TEXT}</span>
        </div>
    );
};

const RetailMatchHouseholdComposition: FunctionComponent<IRetailMatchChartSection>
    = (props: IRetailMatchChartSection) => {
        const { chartData, locationAddress } = props;

        const overviewRecord = brandOverviewRecord(
            props.brand.brandId,
            props.chartData.overview
        );

        const sampleCount: number = overviewRecord?.sampleCount;
        const brandName: string = props.brand.brandName;

        const brandConsumerViewRollups
            = props.chartData.brandConsumerViewRollups?.find((rollups) => {
                return rollups.id === props.brand.brandId;
            });

        return (
            <ReportScrollableSection id="RMHouseholdComposition">
                <ReportSectionHeader>
                    <ReportSectionNumber>04</ReportSectionNumber>
                    <ReportH1>Household Composition</ReportH1>
                </ReportSectionHeader>
                <AnimatedReportCard
                    scrollableParentSelector="#RetailMatchReportBody"
                    animateIn="zoomIn"
                    offset={100}
                    animateOnce={true}
                >
                    <p style={{ color: "black", lineHeight: "25px", fontSize: "15px" }}>
                        <TextFocus>
                            Household Characteristics of {locationAddress}
                        </TextFocus>{" "}
                        <span style={{ fontSize: "20px", letterSpacing: "1.3px" }}>
                            COMPARED TO
                        </span>{" "}
                        <TextFocus>
                            the {overviewRecord?.sampleCount.toLocaleString()} Existing
                            Similar {overviewRecord?.brandName} Locations
                        </TextFocus>
                        <br />
                        {HH_ATTR_STATIC_TEXT}
                    </p>
                    <ThreeColumnGrid>
                        <ReportChartGroup
                            title="Marriage"
                            tooltipText={`"Married" households index [marriageSel1NatHigherLower] the national average at [marriageSel1Value] vs [marriageNationalValue]`}
                            tooltipText2={`“Married” households index [marriageSel1Sel2HigherLower] the ${sampleCount} ${brandName} locations at [marriageSel1Value] vs. [marriageSel2Value].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.maritalStatus,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups.maritalStatus,
                                    national: chartData.nationalConsumerViewRollups.maritalStatus,
                                })
                            )}
                            xTickFormatter={formatMarriedSingle}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.Marriage}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />
                        <ReportChartGroup
                            title="Children"
                            tooltipText={`Households with children index [childrenSel1NatHigherLower] the national average at [childrenSel1Value] vs [childrenNationalValue]`}
                            tooltipText2={`Households with children index [childrenSel1Sel2HigherLower] the ${sampleCount} ${brandName} locations at [childrenSel1Value] vs. [childrenSel2Value].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.childrenPresent,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups
                                            .childrenPresent,
                                    national:
                                        chartData.nationalConsumerViewRollups.childrenPresent,
                                }),
                                false,
                                sortUYN
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            xTickFormatter={formatUYN}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.Children}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="# of Persons in Household"
                            tooltipText="Households with [sel1NatTopId] [personPeopleSel1Nat] index the highest vs. the national average at [sel1NatCompSel1TopValue] vs [sel1NatCompNatTopValue]."
                            tooltipText2={`Households with [sel1Sel2TopId] [personPeopleSel1Sel2] index the highest vs. the ${sampleCount} ${brandName} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.numPersonsInHh,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups
                                            .numPersonsInHh,
                                    national:
                                        chartData.nationalConsumerViewRollups.numPersonsInHh,
                                })
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.NumberOfPersons}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="Head of Household Age"
                            tooltipText="Households with head of household age [sel1NatTopId] index the highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                            tooltipText2={`Households with head of household age [sel1Sel2TopId] index the highest vs. ${sampleCount} ${brandName} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.hohAge,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups.hohAge,
                                    national: chartData.nationalConsumerViewRollups.hohAge,
                                }),
                                true
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.HeadOfHouseholdAge}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="Race & Ethnicity"
                            tooltipText="[sel1NatTopId] households index the highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                            tooltipText2={`[sel1Sel2TopId] households index the highest vs. the ${sampleCount} ${brandName} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.hohEthnicGroup,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups
                                            .hohEthnicGroup,
                                    national:
                                        chartData.nationalConsumerViewRollups.hohEthnicGroup,
                                }),
                                true
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            makeXLabelsFitDemmit={true}
                            xAxisHeight={100}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.RaceAndEthnicity}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="Income"
                            tooltipText="Households with income [sel1NatTopId] index the highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                            tooltipText2={`Households with income [sel1Sel2TopId] index the highest vs. the ${sampleCount} ${brandName} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.hhIncome,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups.hhIncome,
                                    national: chartData.nationalConsumerViewRollups.hhIncome,
                                }),
                                true
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            makeXLabelsFitDemmit={true}
                            xAxisHeight={90}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.HouseholdIncome}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="Education"
                            tooltipText="Households with a [sel1NatTopId] index highest vs. the national average at [sel1NatCompSel1TopValue] vs. [sel1NatCompNatTopValue]."
                            tooltipText2={`Households with a [sel1Sel2TopId] index the highest vs. the ${sampleCount} ${brandName} locations at [sel1Sel2CompSel1TopValue] vs. [sel1Sel2CompSel2TopValue].`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1:
                                        chartData.siteConsumerViewRollups.hohEducationLevel,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups
                                            .hohEducationLevel,
                                    national:
                                        chartData.nationalConsumerViewRollups.hohEducationLevel,
                                }),
                                false,
                                sortEducation
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            xTickFormatter={formatEducation}
                            makeXLabelsFitDemmit={true}
                            xAxisHeight={100}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.HouseholdEducationLevel}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="Dwelling Type"
                            tooltipText={`The greatest % of households observed at ${locationAddress} live in [greatestSel1Id] dwelling units at [greatestSel1Value]. Households in a single family dwelling unit index [dwellingTypeSel1NatHigherLower] the national average of [dwellingTypeNatValue].`}
                            tooltipText2={`Households in single family dwelling units index [dwellingTypeSel1Sel2HigherLower] than the ${sampleCount} ${brandName} locations ([greatestSel1CompSel2Value]).`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.dwellingType,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups.dwellingType,
                                    national: chartData.nationalConsumerViewRollups.dwellingType,
                                }),
                                false,
                                sortDwellingType
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            xTickFormatter={formatDwelling}
                            primaryWeightLabel="Visits"
                            secondaryWeightLabel="Visitors"
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.DwellingType}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />

                        <ReportChartGroup
                            title="Home Ownership"
                            tooltipText={`The greatest % of households observed at ${locationAddress} [greatestOwnSel1Id] their home at [greatestSel1Value]. Households who own index [ownSel1NatHigherLower] the national average of [ownNationalValue].`}
                            tooltipText2={`Households who own index [ownSel1Sel2HigherLower] than the ${sampleCount} ${brandName} locations ([ownSel2Value]).`}
                            data={sortMergedChartData(
                                mergeRetailMatchData({
                                    selection1: chartData.siteConsumerViewRollups.homeOwnership,
                                    selection2:
                                        brandConsumerViewRollups?.consumerViewRollups.homeOwnership,
                                    national: chartData.nationalConsumerViewRollups.homeOwnership,
                                })
                            )}
                            initialYValue="selection1PrimaryWeight"
                            primaryColor={colorTwoBright}
                            secondaryColor={colorTwoDark}
                            scrollableParentSelector="#RetailMatchReportBody"
                            chartAnim="zoomIn"
                            selection1Name={locationAddress}
                            selection2Name={brandName}
                            app={DigitalShareAppType.RetailMatch}
                            itemType={DigitalShareItemType.HomeOwnership}
                            renderType={props.renderType}
                            chartHeight={
                                props.renderType == RenderType.DIGITAL_SHARE && CHILD_HEIGHT
                            }
                            shareText={() =>
                                getChartText(sampleCount, locationAddress, brandName)
                            }
                        />
                    </ThreeColumnGrid>
                </AnimatedReportCard>
            </ReportScrollableSection>
        );
    };

export default RetailMatchHouseholdComposition;
